<template>
  <div data-test-id="add-message-side-sheet">
    <v-navigation-drawer
      :value="value"
      temporary
      fixed
      right
      width="502"
      @input="$emit('input', $event)"
    >
      <DrawerToolbar
        admin
        :title="drawerTitle"
        :show-back-button="true"
        :show-close-button="false"
        @back="handleBack"
      />

      <div v-if="view === 'form'" class="form-view">
        <div>
          <Button
            outlined
            iconLeft="mdi-eye"
            :disabled="!canPreview"
            data-test-id="message-preview-button"
            @click="showPreview = true"
          >
            Preview
          </Button>
        </div>

        <v-form ref="form" v-model="valid" @submit.prevent="handleSubmit">
          <div class="form-fields">
            <div class="timeline-row">
              <Typography variant="subtitle-1" as="span">Send</Typography>
              <v-text-field
                v-model="form.scheduleValue"
                outlined
                dense
                color="brandPrimary"
                hide-details
                type="number"
                min="0"
                step="1"
                placeholder="0 days"
                class="days-field"
                :rules="scheduleValueRules"
                @input="scheduleValueTouched = true"
              />
              <v-select
                v-model="form.scheduleOption"
                outlined
                dense
                color="brandPrimary"
                hide-details
                :items="scheduleOptions"
                item-text="label"
                item-value="value"
                placeholder="after program start"
                class="when-field"
                :rules="requiredRule"
                :error="!!scheduleValueError"
              />
              <Button
                icon
                @click="view = 'help'"
                color="brandPrimary"
                data-test-id="message-scheduling-help"
              >
                <Icon icon="mdi-help-circle" small color="brandPrimary" />
              </Button>
            </div>
            <Alert
              v-if="scheduleValueError"
              variant="error"
              :message="scheduleValueError"
              data-test-id="schedule-value-error"
            />

            <v-text-field
              v-model="form.messageSubject"
              outlined
              color="brandPrimary"
              hide-details="auto"
              label="Subject"
              :rules="subjectRules"
            />

            <div class="body-field">
              <VueEditor
                ref="bodyEditor"
                v-model="form.messageBody"
                :editorToolbar="editorToolbar"
                :editorOptions="editorOptions"
                placeholder="Body content"
              />
              <Typography
                v-if="showBodyError"
                variant="caption-2"
                class="error--text body-field__error"
                data-test-id="message-body-error"
              >
                Message body is required
              </Typography>
            </div>

            <v-text-field
              v-model="form.messageShortBody"
              outlined
              color="brandPrimary"
              hide-details="auto"
              label="Abbreviated message (optional)"
              persistent-hint
              hint="This is displayed when there are content length limits, like with text message and push notifications"
              :rules="shortBodyRules"
            />

            <MessagePersonalizationAccordion
              :program-related="true"
              :program-has-end-date="programLength != null"
              @insert="insertShortcode"
            />
          </div>
        </v-form>

        <div class="actions">
          <Button text @click="handleCancel">Cancel</Button>
          <Button @click="handleSubmit">
            {{ submitButtonText }}
          </Button>
        </div>
      </div>

      <div v-else class="help-view">
        <Typography variant="body-2">
          Program messages scheduled here are time-based and set around the program's start or end
          date. You can schedule messages to go out X days after the program starts, or before the
          program ends.
        </Typography>

        <Typography variant="subtitle-2">Examples:</Typography>
        <Typography variant="body-2">
          To send a welcome email on the first day of the program, you would enter '0' and select
          'days after program start'.
        </Typography>
        <Typography variant="body-2">
          To send a reminder the day before the program ends, you would enter '1' and select 'days
          before program end'.
        </Typography>

        <Typography variant="subtitle-2">Notes:</Typography>
        <Typography variant="body-2">
          The time of day messages are sent is dependent upon the time of day the program starts or
          ends for messages based on program start and program end, respectively. For example, if
          the program starts at 1pm, your message for 2 days after program start will be sent at
          1pm, 48 hours after the start.
        </Typography>
        <Typography variant="body-2">
          For rolling programs, the timing is based on the start or end date for each user in the
          program, which means users may receive messages at different times!
        </Typography>

        <div class="actions">
          <Button @click="view = 'form'">Close</Button>
        </div>
      </div>

      <MessagePreviewSheet
        v-model="showPreview"
        :messageSubject="form.messageSubject"
        :messageShortBody="form.messageShortBody"
        :messageBody="form.messageBody"
        context="program"
      />
    </v-navigation-drawer>
    <v-overlay :value="value" />
  </div>
</template>

<script>
import { VueEditor } from "vue2-editor";
import Alert from "@/components/generic/Alert.vue";
import DrawerToolbar from "@/components/generic/DrawerToolbar.vue";
import Typography from "@/components/generic/Typography.vue";
import Button from "@/components/generic/Button.vue";
import Icon from "@/components/generic/Icon.vue";
import MessagePreviewSheet from "@/components/communications-admin/MessagePreviewSheet.vue";
import MessagePersonalizationAccordion from "@/components/MessagePersonalizationAccordion.vue";
import { editorToolbar, editorOptions } from "@/shared_data/data";

const BLANK_FORM = {
  id: null,
  scheduleValue: null,
  scheduleOption: "AFTER_START",
  messageSubject: "",
  messageShortBody: "",
  messageBody: "",
};

// Quill leaves this exact markup behind when the editor is emptied — treat it
// (and anything falsy) as an empty body for validation purposes.
const EMPTY_BODY_RE = /^<p>(<br>|\s)*<\/p>\s*$/i;
const isBodyEmpty = (html) => !html || EMPTY_BODY_RE.test(html);

export default {
  name: "AddMessageSideSheet",
  components: {
    VueEditor,
    Alert,
    DrawerToolbar,
    Typography,
    Button,
    Icon,
    MessagePreviewSheet,
    MessagePersonalizationAccordion,
  },
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    // Pre-populates the form when editing an existing message. `null` starts a
    // fresh Add-Message flow.
    message: {
      type: Object,
      default: null,
    },
    // Upper bound for scheduleValue in days; null = no upper bound.
    programLength: {
      type: Number,
      default: null,
    },
  },
  emits: ["input", "submit", "cancel"],
  data() {
    return {
      view: "form",
      valid: false,
      // Manual error flag for the Quill body — VueEditor isn't a Vuetify input,
      // so it can't participate in v-form rules. Set true only after a submit
      // attempt with an empty body, cleared as soon as the user starts typing
      // (or when the sheet re-opens).
      showBodyError: false,
      // Mirrors Vuetify's "touched" behavior for the scheduleValue field so the
      // shared error Alert (and the paired select's red border) only surface
      // after the user interacts with the field or attempts submit.
      scheduleValueTouched: false,
      // Fullscreen preview sheet visibility — opens via the Preview button and
      // renders the current form values through MessagePreviewSheet's tabs.
      showPreview: false,
      form: { ...BLANK_FORM },
      editorToolbar,
      editorOptions,
      scheduleOptions: [
        { value: "AFTER_START", label: "after program start" },
        { value: "BEFORE_END", label: "before program end" },
      ],
    };
  },
  computed: {
    isEditing() {
      return !!this.message?.id;
    },
    drawerTitle() {
      if (this.view === "help") return "Message Scheduling";
      return this.isEditing ? "Edit Message" : "Add Message";
    },
    submitButtonText() {
      return this.isEditing ? "Update Message" : "Add Message";
    },
    canPreview() {
      return !!this.form.messageSubject && !isBodyEmpty(this.form.messageBody);
    },
    requiredRule() {
      return [(v) => !!v || "Required"];
    },
    subjectRules() {
      return [
        (v) => !!v || "Subject is required",
        (v) => (v || "").length < 100 || "Subject should be shorter than 100 characters",
      ];
    },
    shortBodyRules() {
      return [(v) => !v || v.length < 100 || "Abbreviated message should be shorter"];
    },
    scheduleValueRules() {
      return [
        (v) => v === 0 || v === "0" || !!v || "A value is required",
        (v) => Number(v) >= 0 || "The value must be 0 or greater",
        (v) => !String(v).includes(".") || "The value must be a whole number",
        (v) =>
          this.programLength == null ||
          Number(v) <= this.programLength ||
          `Enter a value less than the program length (${this.programLength} days) — this message won't be sent otherwise.`,
      ];
    },
    scheduleValueError() {
      if (!this.scheduleValueTouched) return null;
      for (const rule of this.scheduleValueRules) {
        const result = rule(this.form.scheduleValue);
        if (typeof result === "string") return result;
      }
      return null;
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.view = "form";
        this.showBodyError = false;
        this.resetForm();
        this.$nextTick(() => {
          if (this.isEditing) {
            this.scheduleValueTouched = true;
            this.$refs.form?.validate();
          } else {
            this.$refs.form?.resetValidation();
          }
        });
      }
    },
    "form.messageBody"(next) {
      // Clear the manual body-required error as soon as real content lands.
      if (!isBodyEmpty(next)) this.showBodyError = false;
    },
  },
  methods: {
    resetForm() {
      if (!this.message) {
        this.form = { ...BLANK_FORM };
        return;
      }
      // Messages travel in backend shape (numDaysAfterProgramStart /
      // numDaysToProgramEnd). Fold those into the sheet's unified scheduleValue
      // + scheduleOption so the two-field form can render them.
      const isBeforeEnd = this.message.numDaysToProgramEnd != null;
      this.form = {
        ...BLANK_FORM,
        ...this.message,
        scheduleValue: isBeforeEnd
          ? this.message.numDaysToProgramEnd
          : this.message.numDaysAfterProgramStart,
        scheduleOption: isBeforeEnd ? "BEFORE_END" : "AFTER_START",
      };
    },
    handleBack() {
      if (this.view === "help") {
        this.view = "form";
        return;
      }
      this.close();
    },
    handleCancel() {
      this.$emit("cancel");
      this.close();
    },
    handleSubmit() {
      const formValid = this.$refs.form?.validate() ?? true;
      const bodyEmpty = isBodyEmpty(this.form.messageBody);
      this.showBodyError = bodyEmpty;
      // Ensure the shared schedule Alert surfaces on submit even when the user
      // never touched the days field (e.g. hit submit on an empty new form).
      this.scheduleValueTouched = true;
      if (!formValid || bodyEmpty) return;
      // Unfold the sheet's two-field schedule back into the backend's two
      // nullable day-offset columns; strip the UI-only helpers so the payload
      // matches what ProgramMessagesStep stores in `messages`.
      // eslint-disable-next-line no-unused-vars
      const { scheduleValue, scheduleOption, ...rest } = this.form;
      const numeric = scheduleValue === "" || scheduleValue == null ? null : Number(scheduleValue);
      const payload = {
        ...rest,
        numDaysAfterProgramStart: scheduleOption === "AFTER_START" ? numeric : null,
        numDaysToProgramEnd: scheduleOption === "BEFORE_END" ? numeric : null,
      };
      this.$emit("submit", payload);
      this.close();
    },
    close() {
      this.$emit("input", false);
    },
    insertShortcode(code) {
      const editor = this.$refs.bodyEditor?.quill;
      if (!editor) return;
      const selection = editor.getSelection(true);
      editor.insertText(selection.index, `{{{${code}}}}`);
    },
  },
};
</script>

<style scoped>
.form-view,
.help-view {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 16px;
}

.form-fields {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.timeline-row {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
}

.days-field {
  width: 100px;
  flex: 0 0 100px;
}

.when-field {
  width: 220px;
  flex: 0 0 220px;
}

.body-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.actions {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 24px;
  padding-top: 8px;
}
</style>
